import Box from '@mui/material/Box';
import { useCallback } from 'react';
import React from 'react';
import Markdown from '../../components/Markdown';
import FHGTextField from '../../components/TextField';
import ModalDialog from '../../fhg/components/dialog/ModalDialog';
import FHGTypography from '../../fhg/components/Typography';
import useEditData from '../../fhg/components/edit/useEditData';

/**
 * Component to edit a single FAQ row (question / answer / display order).
 * The answer is authored in Markdown; a live preview is rendered below the field.
 *
 * @param faqValue The FAQ row to edit.
 * @param onSubmit Submit the changes to the FAQ row.
 * @param onClose Close the edit dialog.
 * @return {JSX.Element}
 * @constructor
 */
export default function FaqEdit({ faqValue, onSubmit, onClose }) {

   const [editValues, handleChange, { isChanged, getValue, defaultValues }] = useEditData(faqValue);

   /**
    * Handle numeric input, digits only.
    */
   const handleSortOrderChange = useCallback((event) => {
      const value = event.target.value;
      if (value === '' || /^\d*$/.test(value)) {
         handleChange(event);
      }
   }, [handleChange]);

   /**
    * Submit the changes to the FAQ row.
    */
   const handleSubmit = useCallback((event) => {

      if (event) {
         event.preventDefault();
         event.stopPropagation();
      }

      if (isChanged) {
         onSubmit && onSubmit({ ...defaultValues, ...editValues });
      }
   }, [editValues, defaultValues, isChanged, onSubmit]);

   const answer = getValue('answer') || '';

   return (
      <ModalDialog open={true} titleKey={'faq.edit.title'} onSubmit={(event) => handleSubmit(event)} maxWidth={'md'}
         fullWidth onClose={onClose}>
         <FHGTextField
            name='question'
            type='text'
            autoFocus
            multiline
            minRows={2}
            label={'Question'}
            style={{ marginBottom: 8 }}
            onChange={handleChange}
            value={getValue('question') || ''}
            required
         />
         <FHGTextField
            name='answer'
            type='text'
            multiline
            minRows={6}
            label={'Answer'}
            style={{ marginBottom: 4 }}
            onChange={handleChange}
            value={answer}
            required
         />
         <FHGTypography id={'faq.answer.markdownHint'} component={'div'} variant={'caption'} color={'textSecondary'} />
         <Box mt={1} mb={1}>
            <FHGTypography id={'faq.answer.previewLabel'} component={'div'} variant={'caption'} color={'textSecondary'} />
            <Box
               sx={{
                  mt: 0.5,
                  p: 1,
                  minHeight: 60,
                  maxHeight: 200,
                  overflowY: 'auto',
                  border: '1px solid #E0E0E0',
                  borderRadius: 1,
                  backgroundColor: '#FAFAFA',
               }}
            >
               <Markdown>{answer}</Markdown>
            </Box>
         </Box>
         <FHGTextField
            name='sortOrder'
            type='number'
            label={'Order'}
            step='1'
            inputProps={{
               min: '0',
               step: '1'
            }}
            style={{ marginBottom: 8 }}
            onChange={handleSortOrderChange}
            value={getValue('sortOrder') ?? ''}
            required
         />
      </ModalDialog>
   );
}
